import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { io } from 'socket.io-client';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import ProcessDetailPage from './components/ProcessDetailPage';
import MainDashboard from './components/MainDashboard';
import ConfirmationDialog from './components/ConfirmationDialog';
import DeployApplication from './components/DeployApplication';
import ModuleManagement from './components/ModuleManagement';
import EcosystemGenerator from './components/EcosystemGenerator';
import ProcessConfiguration from './components/ProcessConfiguration';
import ClusterManagement from './components/ClusterManagement';
import LogStreamEnhanced from './components/LogStreamEnhanced';
import SidebarMenu from './components/SidebarMenu';
import Settings from './components/Settings';
import LoadBalancingGuide from './components/LoadBalancingGuide';
import RemoteConnections from './components/RemoteConnections';
import CronJobsPage from './components/CronJobsPage';
import MetricsPage from './components/MetricsPage';
import ServerSwitcher from './components/ServerSwitcher';
import PasswordGate from './components/PasswordGate';
import WhatsNew from './components/WhatsNew';
import WhatsNewModal, { shouldShowWhatsNew, markWhatsNewSeen } from './components/WhatsNewModal';
import StatusBar, { Notification } from './components/StatusBar';
import LanguageSwitcher from './components/LanguageSwitcher';
import { getToken } from './auth';
import {
  ACTIVE_SERVER_STORAGE_KEY,
  LOCAL_SERVER_ID,
  REMOTE_CONNECTIONS_CHANGED_EVENT,
  isRemoteServerAvailable,
  resolveAvailableServerId,
} from './utils/server-selection';
import {
  ACCENT_CHANGED_EVENT,
  ACCENT_STORAGE_KEY,
  AccentColor,
  THEME_STORAGE_KEY,
  getAccentPalette,
  getThemePreference,
  isDarkTheme,
  normalizeAccentColor,
  normalizeThemePreference,
} from './utils/theme';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GitHubIcon from '@mui/icons-material/GitHub';
import { PM2Process, SystemMetricsData, ConfirmationDialogData } from './types/pm2';
import { RemoteConnection } from './types/remote';
import {
  MoonIcon,
  SunIcon,
  Bars3Icon,
  ArrowUpCircleIcon,
  StarIcon,
  LockClosedIcon,
  ShieldExclamationIcon,
  SparklesIcon,
  InformationCircleIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

// Initialize socket connection with improved settings
// process.env.REACT_APP_API_URL is baked in at build time by CRA from .env.local / .env
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3101';

const socket = io(API_URL, {
  // Send the session token (if any) on every (re)connection attempt
  auth: (cb: (data: { token?: string }) => void) => cb({ token: getToken() || undefined }),
  // Reconnection settings
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  reconnectionAttempts: Infinity,
  // Connection timeout settings - increased for VPN/remote scenarios
  timeout: 20000,  // Increased from 10s to 20s for high-latency connections
  // Transport configuration
  transports: ['websocket', 'polling'],
  // Upgrade timeout
  upgrade: true,
  // Force new connection
  forceNew: false
});

// @group ServerSwitcher : Read persisted active server selection
const getStoredActiveServerId = (): string =>
  localStorage.getItem(ACTIVE_SERVER_STORAGE_KEY) || LOCAL_SERVER_ID;

// @group Theme : Read persisted color theme selection
const getStoredDarkMode = (): boolean =>
  isDarkTheme(normalizeThemePreference(localStorage.getItem(THEME_STORAGE_KEY)));

const getStoredAccentColor = (): AccentColor =>
  normalizeAccentColor(localStorage.getItem(ACCENT_STORAGE_KEY));

const App: React.FC = () => {
  const { t } = useTranslation();
  // State for process data
  const [processes, setProcesses] = useState<PM2Process[]>([]);
  const [filteredProcesses, setFilteredProcesses] = useState<PM2Process[]>([]);
  
  // State for metrics data
  const [metrics, setMetrics] = useState<SystemMetricsData>({
    loadAvg: [0, 0, 0],
    memory: { total: 0, free: 0, used: 0 },
    uptime: 0,
    cpus: 0
  });
  
  // UI state
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const notifCounterRef = useRef(0);
  const [socketConnected, setSocketConnected] = useState<boolean>(socket.connected);
  const enqueueNotification = useCallback((message: string, type: Notification['type'] = 'error') => {
    const id = `notif-${++notifCounterRef.current}`;
    setNotifications(prev => [...prev, { id, message, type }]);
  }, []);
  const dismissNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [namespaceFilter, setNamespaceFilter] = useState<string>('all');
  const [showAbout, setShowAbout] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(
    () => localStorage.getItem('ezpm2_sidebar_collapsed') === '1'
  );

  // @group Updates : Silently check for a newer npm version after initial load
  const [updateAvailable, setUpdateAvailable] = useState<boolean>(false);
  const [appVersion, setAppVersion] = useState<string>('');

  // @group Auth : Password-protection state
  // null = not yet fetched, false = no password set, true = password set
  const [passwordSet,  setPasswordSet]  = useState<boolean | null>(null);
  const [pinSet,       setPinSet]       = useState<boolean | null>(null);
  // Persist unlock across tabs and page refreshes
  const [appUnlocked,  setAppUnlocked]  = useState<boolean>(
    () => localStorage.getItem('ezpm2_unlocked') === '1'
  );
  const autoLockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // @group WhatsNew : Show popup once per session
  const [showWhatsNew, setShowWhatsNew] = useState<boolean>(false);
  
  // @group Theme : Persisted dark/light mode state
  const [darkMode, setDarkMode] = useState<boolean>(getStoredDarkMode);
  const [accentColor, setAccentColor] = useState<AccentColor>(getStoredAccentColor);
  const accentPalette = getAccentPalette(accentColor);

  // @group Theme : Sync document theme hooks for Tailwind, MUI, accent colors, and CSS overrides
  useEffect(() => {
    const theme = getThemePreference(darkMode);
    document.documentElement.classList.toggle('dark', darkMode);
    document.documentElement.dataset.theme = theme;
    document.documentElement.dataset.accent = accentColor;
    document.documentElement.style.colorScheme = theme;
    document.documentElement.style.setProperty('--ez-accent', accentPalette.main);
    document.documentElement.style.setProperty('--ez-accent-hover', accentPalette.hover);
    document.documentElement.style.setProperty('--ez-accent-muted', accentPalette.muted);
    document.documentElement.style.setProperty('--ez-accent-soft', accentPalette.soft);
    document.documentElement.style.setProperty('--ez-accent-strong-bg', accentPalette.strongBg);
    document.documentElement.style.setProperty('--ez-accent-border', accentPalette.border);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    localStorage.setItem(ACCENT_STORAGE_KEY, accentColor);
  }, [accentColor, accentPalette, darkMode]);

  // @group Theme : React to accent color changes from Settings without a reload
  useEffect(() => {
    const handleAccentChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ accentColor?: string }>).detail;
      setAccentColor(normalizeAccentColor(detail?.accentColor));
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === ACCENT_STORAGE_KEY) {
        setAccentColor(normalizeAccentColor(event.newValue));
      }
    };

    window.addEventListener(ACCENT_CHANGED_EVENT, handleAccentChanged);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(ACCENT_CHANGED_EVENT, handleAccentChanged);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  // @group ServerSwitcher : Active server state and remote connections list
  const [activeServerId, setActiveServerId] = useState<string>(
    getStoredActiveServerId
  );
  const activeServerIdRef = useRef<string>(getStoredActiveServerId());
  const [remoteConnections, setRemoteConnections] = useState<RemoteConnection[]>([]);
  const [remoteConnectionsLoaded, setRemoteConnectionsLoaded] = useState<boolean>(false);

  // @group ServerSwitcher : Persist active server selection consistently
  const persistActiveServerId = useCallback((serverId: string): void => {
    activeServerIdRef.current = serverId;
    setActiveServerId(serverId);
    localStorage.setItem(ACTIVE_SERVER_STORAGE_KEY, serverId);
  }, []);

  // @group DataFetching : Local server process and metrics loader
  const loadLocalProcessesAndMetrics = useCallback(async (): Promise<void> => {
    const [processesRes, metricsRes] = await Promise.all([
      axios.get<PM2Process[]>('/api/processes'),
      axios.get<SystemMetricsData>('/api/metrics'),
    ]);
    setProcesses(processesRes.data);
    setMetrics(metricsRes.data);
  }, []);

  // @group ServerSwitcher : Refresh configured remote server list
  const refreshRemoteConnections = useCallback(async (): Promise<void> => {
    try {
      const res = await axios.get<RemoteConnection[]>('/api/remote/connections');
      setRemoteConnections(res.data);
      setRemoteConnectionsLoaded(true);
    } catch {
      // silent — connections list is non-critical
    }
  }, []);

  // Confirmation dialog state
  const [confirmationDialog, setConfirmationDialog] = useState<ConfirmationDialogData>({
    isOpen: false,
    title: '',
    message: '',
    action: '',
    processId: null
  });
  useEffect(() => {
    // Track last data update timestamp to detect if data is actually flowing
    let lastDataUpdate = Date.now();
    let connectionErrorTimeout: ReturnType<typeof setTimeout> | null = null;
    let socketConnected = socket.connected;

    // Initial data fetch (local processes and metrics)
    const fetchInitialData = async (): Promise<void> => {
      try {
        await loadLocalProcessesAndMetrics();
        setLoading(false);
        lastDataUpdate = Date.now();
      } catch (err: any) {
        console.error('Error fetching initial data:', err);

        // Check if it's a PM2 not installed error
        if (err.response?.data?.pmNotInstalled) {
          enqueueNotification(err.response.data.error || t('errors.pm2NotInstalled'));
        } else {
          enqueueNotification(t('errors.failedToConnect'));
        }

        setLoading(false);
      }
    };

    fetchInitialData();

    // Set up socket listeners for real-time updates
    // @group ServerSwitcher : Only apply socket process updates when viewing local server
    socket.on('processes', (data: PM2Process[]) => {
      if (activeServerIdRef.current !== 'local') return;
      setProcesses(data);
      lastDataUpdate = Date.now();
    });

    socket.on('metrics', (data: SystemMetricsData) => {
      setMetrics(data);
      lastDataUpdate = Date.now();
    });

    // Handle connection errors with debouncing
    socket.on('connect_error', () => {
      console.warn('Socket connect_error event detected');

      const timeSinceLastUpdate = Date.now() - lastDataUpdate;

      if (timeSinceLastUpdate > 5000) {
        if (connectionErrorTimeout) {
          clearTimeout(connectionErrorTimeout);
        }

        connectionErrorTimeout = setTimeout(() => {
          if (Date.now() - lastDataUpdate > 5000 && !socketConnected) {
            enqueueNotification(t('errors.connectionLost'), 'warn');
          }
        }, 3000);
      }
    });

    // Handle successful connection
    socket.on('connect', () => {
      console.log('Socket connected successfully');
      socketConnected = true;
      setSocketConnected(true);
      lastDataUpdate = Date.now();

      if (connectionErrorTimeout) {
        clearTimeout(connectionErrorTimeout);
        connectionErrorTimeout = null;
      }
    });

    // Handle disconnection
    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      socketConnected = false;
      setSocketConnected(false);

      if (reason === 'io server disconnect' || reason === 'transport close') {
        if (connectionErrorTimeout) {
          clearTimeout(connectionErrorTimeout);
        }

        connectionErrorTimeout = setTimeout(() => {
          if (!socketConnected && Date.now() - lastDataUpdate > 5000) {
            enqueueNotification(t('errors.connectionLost'), 'warn');
          }
        }, 5000);
      }
    });

    return () => {
      // Cleanup
      if (connectionErrorTimeout) {
        clearTimeout(connectionErrorTimeout);
      }
      
      socket.off('processes');
      socket.off('metrics');
      socket.off('connect_error');
      socket.off('connect');
      socket.off('disconnect');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueNotification, loadLocalProcessesAndMetrics]);

  // Filter processes when search, status, or namespace filter changes
  useEffect(() => {
    let result = [...processes];

    // Apply namespace filter
    if (namespaceFilter !== 'all') {
      result = result.filter(p => (p.pm2_env?.namespace || 'default') === namespaceFilter);
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(p => p.pm2_env.status === statusFilter);
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(searchLower) ||
        String(p.pm_id).includes(searchLower)
      );
    }

    setFilteredProcesses(result);
  }, [processes, searchTerm, statusFilter, namespaceFilter]);

  // @group ServerSwitcher : Fall back to local when persisted remote selection no longer exists
  useEffect(() => {
    if (!remoteConnectionsLoaded || activeServerId === LOCAL_SERVER_ID) return;

    const nextServerId = resolveAvailableServerId(activeServerId, remoteConnections);
    if (nextServerId === activeServerId) return;

    console.warn(`Active remote server "${activeServerId}" no longer exists; switching to local.`);
    setProcesses([]);
    persistActiveServerId(nextServerId);
    loadLocalProcessesAndMetrics().catch((err) => {
      console.error('Error restoring local processes after remote removal:', err);
    });
  }, [
    activeServerId,
    loadLocalProcessesAndMetrics,
    persistActiveServerId,
    remoteConnections,
    remoteConnectionsLoaded,
  ]);

  // @group ServerSwitcher : Poll remote processes when a remote server is active
  useEffect(() => {
    if (activeServerId === LOCAL_SERVER_ID || !remoteConnectionsLoaded) return;
    if (!isRemoteServerAvailable(activeServerId, remoteConnections)) return;

    const fetchRemoteProcesses = async (): Promise<void> => {
      try {
        const res = await axios.get<PM2Process[]>(`/api/remote/${activeServerId}/processes`);
        setProcesses(res.data);
      } catch (err: any) {
        console.error('Error fetching remote processes:', err);
        const errorMessage = err.response?.data?.error || err.message;

        if (err.response?.status === 404 && errorMessage === 'Connection not found') {
          setProcesses([]);
          persistActiveServerId(LOCAL_SERVER_ID);
          await loadLocalProcessesAndMetrics().catch((localErr) => {
            console.error('Error restoring local processes after missing remote:', localErr);
          });
          return;
        }

        enqueueNotification(t('errors.failedFetchRemote', { error: errorMessage }));
      }
    };

    fetchRemoteProcesses();
    const interval = setInterval(fetchRemoteProcesses, 3000);
    return () => clearInterval(interval);
  }, [
    activeServerId,
    enqueueNotification,
    loadLocalProcessesAndMetrics,
    persistActiveServerId,
    remoteConnections,
    remoteConnectionsLoaded,
    t,
  ]);

  // @group ServerSwitcher : Refresh remote connections list periodically so status dots stay current
  useEffect(() => {
    const refresh = () => {
      void refreshRemoteConnections();
    };
    refresh();

    const interval = setInterval(refresh, 5000);
    window.addEventListener(REMOTE_CONNECTIONS_CHANGED_EVENT, refresh);

    return () => {
      clearInterval(interval);
      window.removeEventListener(REMOTE_CONNECTIONS_CHANGED_EVENT, refresh);
    };
  }, [refreshRemoteConnections]);

  // @group Updates : Check for update once after 4 s — non-blocking, silent on failure
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const res = await fetch('/api/update/check');
        const json = await res.json();
        if (json.success && json.data?.updateAvailable) {
          setUpdateAvailable(true);
        }
      } catch {
        // silent — update check is best-effort
      }
    }, 4000);
    return () => clearTimeout(timer);
  }, []);

  // @group Updates : Load installed app version for footer/about display
  useEffect(() => {
    fetch('/api/update/current')
      .then(res => res.json())
      .then(json => {
        const version = json?.data?.currentVersion;
        if (typeof version === 'string') setAppVersion(version);
      })
      .catch(() => {
        // silent — footer falls back to a loading placeholder
      });
  }, []);

  // @group Auth : Fetch password-protection status on mount; also load autoLockMinutes
  const [autoLockMinutes, setAutoLockMinutes] = useState<number>(0);
  useEffect(() => {
    fetch('/api/auth/status')
      .then(r => r.json())
      .then(j => {
        setPasswordSet(j.passwordSet ?? false);
        setPinSet(j.pinSet ?? false);
        setAutoLockMinutes(j.autoLockMinutes ?? 0);
      })
      .catch(() => { setPasswordSet(false); setPinSet(false); });
  }, []);

  // @group Auth : Sync localStorage when unlock state changes
  useEffect(() => {
    if (appUnlocked) {
      localStorage.setItem('ezpm2_unlocked', '1');
    } else {
      localStorage.removeItem('ezpm2_unlocked');
    }
  }, [appUnlocked]);

  // @group Auth : Auto-lock on inactivity — resets on mouse/key/touch events
  useEffect(() => {
    if (!appUnlocked || autoLockMinutes <= 0) return;

    const ms = autoLockMinutes * 60 * 1000;

    const resetTimer = () => {
      if (autoLockTimerRef.current) clearTimeout(autoLockTimerRef.current);
      autoLockTimerRef.current = setTimeout(() => {
        setAppUnlocked(false);
      }, ms);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(ev => window.addEventListener(ev, resetTimer, { passive: true }));
    resetTimer(); // start the initial timer

    return () => {
      events.forEach(ev => window.removeEventListener(ev, resetTimer));
      if (autoLockTimerRef.current) clearTimeout(autoLockTimerRef.current);
    };
  }, [appUnlocked, autoLockMinutes]);

  // @group Auth : Listen for autoLockMinutes changes dispatched from Settings page
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ autoLockMinutes: number }>).detail;
      setAutoLockMinutes(detail.autoLockMinutes);
    };
    window.addEventListener('ezpm2_autolock_changed', handler);
    return () => window.removeEventListener('ezpm2_autolock_changed', handler);
  }, []);

  // Process control functions
  const handleProcessAction = (id: number, action: string): void => {
    // For destructive actions, show confirmation dialog
    if (['stop', 'delete', 'restart'].includes(action)) {
      const processName = processes.find(p => p.pm_id === id)?.name || `Process ${id}`;
      let title: string, message: string;
      
      switch (action) {
        case 'stop':
          title = 'Stop Process?';
          message = `Are you sure you want to stop ${processName}?`;
          break;
        case 'delete':
          title = 'Delete Process?';
          message = `Are you sure you want to delete ${processName} from PM2?`;
          break;
        case 'restart':
          title = 'Restart Process?';
          message = `Are you sure you want to restart ${processName}?`;
          break;
        default:
          title = 'Confirm Action';
          message = `Are you sure you want to ${action} ${processName}?`;
          break;
      }
      
      setConfirmationDialog({
        isOpen: true,
        title,
        message,
        action,
        processId: id
      });
    } else {
      // For non-destructive actions, proceed immediately
      executeAction(id, action);
    }
  };
  
  // Execute the actual API call — routes to remote API when a remote server is active
  const executeAction = async (id: number, action: string): Promise<void> => {
    try {
      if (activeServerIdRef.current !== 'local') {
        // Find process name by id for the remote API
        const proc = processes.find(p => p.pm_id === id);
        const processName = proc?.name || String(id);
        await axios.post(`/api/remote/${activeServerIdRef.current}/processes/${processName}/${action}`);
      } else {
        await axios.post(`/api/process/${id}/${action}`);
        // The socket will update the process list
      }
    } catch (err) {
      console.error(`Error performing ${action}:`, err);
      enqueueNotification(t('errors.failedProcessAction', { action, error: err instanceof Error ? err.message : 'Unknown error' }));
    }
  };
  
  const toggleAbout = (): void => {
    setShowAbout(!showAbout);
  };

  // @group WhatsNew : Auto-show on first visit this session (only when unlocked/no auth)
  useEffect(() => {
    if (shouldShowWhatsNew()) {
      // Small delay so the app renders first
      const t = setTimeout(() => {
        setShowWhatsNew(true);
        markWhatsNewSeen();
      }, 800);
      return () => clearTimeout(t);
    }
  }, []);
  
  const toggleDarkMode = (): void => {
    const next = !darkMode;
    setDarkMode(next);
  };
  
  const toggleMenu = (): void => {
    setMenuOpen(!menuOpen);
  };

  const toggleSidebar = (): void => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('ezpm2_sidebar_collapsed', next ? '1' : '0');
      return next;
    });
  };

  // @group ServerSwitcher : Switch active server — auto-connect remote, reset processes view
  const handleServerSwitch = async (serverId: string): Promise<void> => {
    if (serverId === activeServerId) return;

    setProcesses([]);
    persistActiveServerId(serverId);

    if (serverId !== LOCAL_SERVER_ID) {
      // Auto-connect if not already connected
      const conn = remoteConnections.find(c => c.id === serverId);
      if (!conn) {
        persistActiveServerId(LOCAL_SERVER_ID);
        try {
          await loadLocalProcessesAndMetrics();
        } catch (err) {
          console.error('Error restoring local processes after unavailable remote switch:', err);
        }
        return;
      }

      if (conn && !conn.connected) {
        try {
          await axios.post(`/api/remote/${serverId}/connect`);
          // Refresh connections list to reflect new connected state
          await refreshRemoteConnections();
        } catch (err: any) {
          enqueueNotification(t('errors.failedConnectTo', { name: conn.name || conn.host, error: err.response?.data?.error || err.message }));
        }
      }
    } else {
      // Switching back to local — restore from socket / re-fetch
      try {
        await loadLocalProcessesAndMetrics();
      } catch (err) {
        console.error('Error restoring local processes:', err);
      }
    }
  };
  
  const handleConfirmAction = (): void => {
    if (confirmationDialog.processId !== null) {
      executeAction(confirmationDialog.processId, confirmationDialog.action);
    }
    setConfirmationDialog(prev => ({ ...prev, isOpen: false }));
  };
  
  const handleCancelAction = (): void => {
    setConfirmationDialog(prev => ({ ...prev, isOpen: false }));
  };
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
  };
  
  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setStatusFilter(e.target.value);
  };

  const handleNamespaceFilterChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setNamespaceFilter(e.target.value);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <div className="flex items-center gap-2.5">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#22c55e] border-t-transparent"></div>
          <span className="text-[#888] font-mono text-xs">
            Loading PM2 data…
          </span>
        </div>
      </div>
    );
  }

  // @group Theme : MUI theme wired to persisted dark/light mode
  const muiTheme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: accentPalette.main,
      },
      background: {
        default: darkMode ? '#0a0a0a' : '#f8fafc',
        paper:   darkMode ? '#111111' : '#ffffff',
      },
      text: {
        primary:   darkMode ? '#e8e8e8' : '#0f172a',
        secondary: darkMode ? '#888888' : '#475569',
      },
      divider: darkMode ? '#1e1e1e' : '#d8dee9',
    },
    typography: {
      fontFamily: 'inherit',          // use the Tailwind / CSS font stack
      h4: { fontSize: '1.0625rem', fontWeight: 600, lineHeight: 1.3 },  // 17px — page titles
      h5: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.3 },  // 15px — section headers
      h6: { fontSize: '0.875rem',  fontWeight: 600, lineHeight: 1.3 },  // 14px — subsections
      subtitle1: { fontSize: '0.8125rem', lineHeight: 1.4 },            // 13px
      subtitle2: { fontSize: '0.75rem',   fontWeight: 600, lineHeight: 1.4 }, // 12px
      body1:     { fontSize: '0.875rem',  lineHeight: 1.5 },            // 14px
      body2:     { fontSize: '0.8125rem', lineHeight: 1.5 },            // 13px
      caption:   { fontSize: '0.75rem',   lineHeight: 1.4 },            // 12px
      overline:  { fontSize: '0.6875rem', lineHeight: 1.4 },            // 11px
    },
    shape: { borderRadius: 6 },
    components: {
      // Buttons — small by default, no shadow
      MuiButton: {
        defaultProps: { size: 'small', disableElevation: true },
        styleOverrides: { root: { textTransform: 'none', fontWeight: 500 } },
      },
      MuiIconButton: { defaultProps: { size: 'small' } },
      // Form controls — small by default
      MuiTextField:   { defaultProps: { size: 'small' } },
      MuiSelect:      { defaultProps: { size: 'small' } },
      MuiFormControl: { defaultProps: { size: 'small' } },
      MuiInputLabel:  {
        defaultProps: { size: 'small' },
        // Fix: Tailwind Preflight resets fieldset/legend, breaking MUI's border notch.
        // Adding a background on the shrunk label visually covers the border line.
        styleOverrides: {
          outlined: ({ theme }: { theme: any }) => ({
            '&.MuiInputLabel-shrink': {
              backgroundColor: theme.palette.background.paper,
              paddingLeft: '4px',
              paddingRight: '4px',
              borderRadius: '2px',
            },
          }),
        },
      },
      // Fix: Tailwind base layer and browsers apply outline/box-shadow on
      // :focus / :focus-visible / :focus-within to every element.
      // For MUI OutlinedInput the visual border is drawn by a child <fieldset>,
      // so any outline on the wrapper div or <input> shows as an extra blue ring.
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&:focus':         { outline: 'none', boxShadow: 'none' },
            '&:focus-visible': { outline: 'none', boxShadow: 'none' },
            '&:focus-within':  { outline: 'none', boxShadow: 'none' },
            '& input:focus':          { outline: 'none', boxShadow: 'none' },
            '& input:focus-visible':  { outline: 'none', boxShadow: 'none' },
            '& textarea:focus':       { outline: 'none', boxShadow: 'none' },
            '& textarea:focus-visible': { outline: 'none', boxShadow: 'none' },
          },
        },
      },
      // Chips — small by default
      MuiChip: { defaultProps: { size: 'small' } },
      // Cards — consistent border radius, outlined by default
      MuiCard:  { defaultProps: { variant: 'outlined' } },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      // Table — dense
      MuiTableCell: {
        styleOverrides: {
          head: { fontWeight: 600, fontSize: '0.75rem' },
          body: { fontSize: '0.8125rem' },
        },
      },
      // Tabs — compact
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', minHeight: 36, fontSize: '0.8125rem', fontWeight: 500 },
        },
      },
      MuiTabs: { styleOverrides: { root: { minHeight: 36 } } },
      // Accordion — compact
      MuiAccordionSummary: {
        styleOverrides: { root: { minHeight: 40, '&.Mui-expanded': { minHeight: 40 } } },
      },
      // Alerts — compact padding
      MuiAlert: { styleOverrides: { root: { fontSize: '0.8125rem', padding: '6px 12px' } } },
      // Dialogs — consistent radius
      MuiDialog: { styleOverrides: { paper: { borderRadius: 8 } } },
    },
  });

  return (
    <ThemeProvider theme={muiTheme}>
    <Router>
      {/* @group Auth : Password/PIN gate — rendered before anything else when locked */}
      {(passwordSet === true || pinSet === true) && !appUnlocked && (
        <PasswordGate
          darkMode={darkMode}
          onUnlock={() => {
            setAppUnlocked(true);
            // Reconnect the socket so the freshly stored token is sent on the handshake
            socket.disconnect();
            socket.connect();
          }}
          pinSet={pinSet ?? false}
          passwordSet={passwordSet ?? false}
        />
      )}

      <div className="min-h-screen bg-[#0a0a0a]">
        <div className="flex">

          {/* ── Top Navigation Bar (36px) ── */}
          <nav className="fixed w-full z-50 h-9 flex items-center border-b bg-[#0a0a0a] border-[#1e1e1e]">
            <div className="flex items-center w-full px-3 gap-2">
              {/* Mobile hamburger / Desktop sidebar collapse toggle */}
              <button
                onClick={toggleMenu}
                className="sm:hidden p-1 rounded text-[#555] hover:text-[#e8e8e8] hover:bg-[#1a1a1a] transition-colors"
              >
                <Bars3Icon className="h-4 w-4" />
              </button>
              <button
                onClick={toggleSidebar}
                title={sidebarCollapsed ? t('header.expandSidebar') : t('header.collapseSidebar')}
                className="hidden sm:flex items-center justify-center p-1 rounded transition-colors text-[#555] hover:text-[#e8e8e8] hover:bg-[#1a1a1a]"
              >
                <Bars3Icon className="h-4 w-4" />
              </button>

              {/* @group Branding : App logo and wordmark */}
              <Link
                to="/"
                className="flex items-center gap-1.5 font-mono text-xs font-bold text-[#e8e8e8] tracking-tight no-underline"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/logo192.svg`}
                  alt=""
                  className="h-4 w-4 rounded-[3px] shrink-0"
                />
                <span>EZ PM2 GUI</span>
              </Link>

              {/* @group Auth : No-password banner — shown when password protection is not yet enabled */}
              {passwordSet === false && (
                <Link
                  to="/settings?section=security"
                  title={t('header.enablePasswordProtection')}
                  className="hidden sm:flex items-center gap-1 px-2 py-0.5 rounded-full no-underline border transition-colors bg-[#2d1500] border-[#f59e0b]/30 text-[#f59e0b] font-mono text-[10px] hover:bg-[#3d1e00]"
                >
                  <ShieldExclamationIcon className="h-3.5 w-3.5" />
                  <span>{t('header.noPasswordSet')}</span>
                </Link>
              )}

              {/* Spacer */}
              <div className="flex-1" />

              {/* @group ServerSwitcher : Active remote server indicator in navbar */}
              {activeServerId !== 'local' && (() => {
                const conn = remoteConnections.find(c => c.id === activeServerId);
                return (
                  <div className="hidden sm:flex items-center gap-1.5 font-mono text-xs">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#22d3ee] animate-pulse shrink-0" />
                    <span className="text-[#22d3ee] font-medium">
                      <strong>{conn?.name || 'Remote Server'}</strong>
                      {conn && <span className="text-[#555] font-normal"> · {conn.username}@{conn.host}</span>}
                    </span>
                    <button
                      onClick={() => handleServerSwitch('local')}
                      className="ml-1 text-xs underline opacity-70 hover:opacity-100 transition-opacity text-[#888]"
                    >
                      {t('header.switchToLocal')}
                    </button>
                  </div>
                );
              })()}

              {/* Right side */}
              <div className="flex items-center gap-1.5">

                {/* @group Updates : Pulse badge — visible when a newer npm version exists */}
                {updateAvailable && (
                  <Link
                    to="/settings"
                    title={t('header.updateAvailable')}
                    className="relative flex items-center gap-1 px-2 py-0.5 rounded no-underline border transition-colors bg-[#1a1200] border-[#f59e0b]/30 text-[#f59e0b] font-mono text-[10px] hover:bg-[#251900]"
                  >
                    <ArrowUpCircleIcon className="h-3.5 w-3.5" />
                    <span>{t('header.update')}</span>
                    <span className="flex h-1.5 w-1.5 ml-0.5">
                      <span className="animate-ping absolute inline-flex h-1.5 w-1.5 rounded-full bg-[#f59e0b] opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#f59e0b]"></span>
                    </span>
                  </Link>
                )}

                {/* @group Auth : Lock button — click to immediately re-lock the app */}
                {passwordSet === true && appUnlocked && (
                  <button
                    onClick={() => setAppUnlocked(false)}
                    title={t('header.lockApp')}
                    className="p-1 rounded transition-colors text-[#22c55e] hover:text-[#ef4444]"
                  >
                    <LockClosedIcon className="h-3.5 w-3.5" />
                  </button>
                )}

                {/* Divider */}
                <span className="h-4 w-px mx-0.5 bg-[#1e1e1e]" />

                {/* @group GitHub : Star button */}
                <a
                  href="https://github.com/thechandanbhagat/ezpm2gui"
                  target="_blank"
                  rel="noopener noreferrer"
                  title={t('header.starTooltip')}
                  className="flex items-center gap-1 px-2 py-0.5 rounded no-underline border transition-colors bg-[#111] border-[#1e1e1e] text-[#888] hover:border-[#f59e0b]/60 hover:text-[#f59e0b] font-mono text-[10px]"
                >
                  <StarIcon className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{t('header.star')}</span>
                </a>

                {/* @group GitHub : Repo link */}
                <a
                  href="https://github.com/thechandanbhagat/ezpm2gui"
                  target="_blank"
                  rel="noopener noreferrer"
                  title={t('header.viewOnGitHub')}
                  className="p-1 rounded transition-colors text-[#555] hover:text-[#e8e8e8]"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                    <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
                  </svg>
                </a>

                {/* Divider */}
                <span className="h-4 w-px mx-0.5 bg-[#1e1e1e]" />

                {/* @group ServerSwitcher : Global server context switcher */}
                <ServerSwitcher
                  activeServerId={activeServerId}
                  connections={remoteConnections}
                  darkMode={darkMode}
                  onSwitch={handleServerSwitch}
                />

                {/* Divider */}
                <span className="h-4 w-px mx-0.5 bg-[#1e1e1e]" />

                {/* What's New */}
                <button
                  onClick={() => setShowWhatsNew(true)}
                  title={t('header.whatsNew')}
                  className="p-1 rounded transition-colors text-[#a78bfa] hover:text-[#c4b5fd]"
                >
                  <SparklesIcon className="h-4 w-4" />
                </button>

                {/* About */}
                <button
                  onClick={toggleAbout}
                  title={t('header.about')}
                  className="p-1 rounded transition-colors text-[#555] hover:text-[#e8e8e8]"
                >
                  <InformationCircleIcon className="h-4 w-4" />
                </button>

                {/* Settings */}
                <Link
                  to="/settings"
                  title={t('header.settings')}
                  className="p-1 rounded transition-colors text-[#555] hover:text-[#e8e8e8]"
                >
                  <Cog6ToothIcon className="h-4 w-4" />
                </Link>

                {/* Dark-mode toggle */}
                <button
                  onClick={toggleDarkMode}
                  className="p-1 rounded transition-colors text-[#555] hover:text-[#e8e8e8]"
                  title={darkMode ? t('header.lightMode') : t('header.darkMode')}
                >
                  {darkMode ? <SunIcon className="h-4 w-4" /> : <MoonIcon className="h-4 w-4" />}
                </button>

                {/* Language Switcher */}
                <LanguageSwitcher darkMode={darkMode} />
              </div>
            </div>
          </nav>

          {/* ── Mobile Sidebar ── */}
          {menuOpen && (
            <div className="fixed inset-0 z-40 sm:hidden">
              <div className="fixed inset-0 bg-black/50" onClick={toggleMenu} />
              <div className="fixed left-0 top-0 h-full w-[200px] border-r shadow-xl bg-[#0d0d0d] border-[#1e1e1e]">
                <div className="pt-9 h-full overflow-y-auto">
                  <SidebarMenu onItemClick={toggleMenu} />
                </div>
              </div>
            </div>
          )}

          {/* ── Desktop Sidebar ── */}
          <div className={`hidden sm:flex flex-col fixed left-0 top-9 z-[40] h-[calc(100vh-2.25rem-22px)] border-r overflow-y-auto transition-[width] duration-200 bg-[#0d0d0d] border-[#1e1e1e] ${
            sidebarCollapsed ? 'w-[44px]' : 'w-[200px]'
          }`}>
            <SidebarMenu collapsed={sidebarCollapsed} />
          </div>

          {/* ── Main Content ── */}
          <main className={`flex-1 pt-9 min-h-screen transition-[margin] duration-200 bg-[#0a0a0a] ${
            sidebarCollapsed ? 'sm:ml-[44px]' : 'sm:ml-[200px]'
          }`}>
            <div className="px-3 py-3 pb-8">

              <Routes>
                <Route path="/" element={
                  <MainDashboard
                    processes={filteredProcesses}
                    metrics={metrics}
                    searchTerm={searchTerm}
                    statusFilter={statusFilter}
                    onSearchChange={handleSearchChange}
                    onStatusFilterChange={handleStatusFilterChange}
                    onNamespaceFilterChange={handleNamespaceFilterChange}
                    namespaceFilter={namespaceFilter}
                    onProcessAction={handleProcessAction}
                  />
                } />

                <Route path="/processes" element={
                  <MainDashboard
                    processes={filteredProcesses}
                    metrics={metrics}
                    searchTerm={searchTerm}
                    statusFilter={statusFilter}
                    onSearchChange={handleSearchChange}
                    onStatusFilterChange={handleStatusFilterChange}
                    onNamespaceFilterChange={handleNamespaceFilterChange}
                    namespaceFilter={namespaceFilter}
                    onProcessAction={handleProcessAction}
                  />
                } />
                <Route path="/process/:id" element={
                  <ProcessDetailPage
                    onAction={handleProcessAction}
                    connectionId={activeServerId !== 'local' ? activeServerId : undefined}
                  />
                } />
                <Route path="/remote" element={<RemoteConnections />} />
                <Route path="/deploy" element={<DeployApplication />} />
                <Route path="/modules" element={<ModuleManagement onNotify={enqueueNotification} />} />
                <Route path="/ecosystem" element={<EcosystemGenerator />} />
                <Route path="/configure/:id" element={<ProcessConfiguration procId={0} />} />
                <Route path="/cluster" element={<ClusterManagement processes={processes} onRefresh={() => {
                  const fetchProcesses = async () => {
                    try {
                      const response = await axios.get<PM2Process[]>('/api/processes');
                      setProcesses(response.data);
                    } catch (err) {
                      console.error('Error refreshing processes:', err);
                      enqueueNotification(t('errors.failedRefreshProcessData'));
                    }
                  };
                  fetchProcesses();
                }} />} />                <Route path="/logs" element={<LogStreamEnhanced />} />
                <Route path="/logs/remote/:serverId/:processId" element={<LogStreamEnhanced />} />
                <Route path="/logs/:id" element={<LogStreamEnhanced />} />
                <Route path="/cron-jobs" element={<CronJobsPage />} />
                <Route path="/metrics" element={<MetricsPage processes={processes} metrics={metrics} />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/load-balancing-guide" element={<LoadBalancingGuide />} />
                <Route path="/whats-new" element={<WhatsNew />} />
              </Routes>
            </div>{/* /px-3 py-3 */}
          </main>
        </div>

        {/* @group StatusBar : Persistent bottom bar with notification queue */}
        <StatusBar
          notifications={notifications}
          onDismiss={dismissNotification}
          status={{
            connected: socketConnected,
            processCount: processes.length,
            onlineCount: processes.filter(p => p.pm2_env.status === 'online').length,
            activeServer: activeServerId,
          }}
          version={appVersion}
        />

        <ConfirmationDialog
          isOpen={confirmationDialog.isOpen}
          title={confirmationDialog.title}
          message={confirmationDialog.message}
          onConfirm={handleConfirmAction}
          onCancel={handleCancelAction}
          type={confirmationDialog.action === 'delete' ? 'danger' : 'warning'}
        />

        {/* @group AboutDialog : About popup dialog */}
        <WhatsNewModal
          open={showWhatsNew}
          onClose={() => setShowWhatsNew(false)}
          darkMode={darkMode}
        />

        <Dialog open={showAbout} onClose={toggleAbout} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
            {/* @group Branding : About dialog app logo */}
            <img
              src={`${process.env.PUBLIC_URL}/logo192.svg`}
              alt=""
              style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0 }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '0.9375rem', lineHeight: 1.3 }}>EZ PM2 GUI</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.5, fontWeight: 400 }}>
                {appVersion ? `v${appVersion}` : 'v...'} · Chandan Bhagat
              </div>
            </div>
            <IconButton size="small" onClick={toggleAbout} sx={{ ml: 'auto' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ pt: 2, pb: 2.5 }}>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.8125rem', lineHeight: 1.8 }}>
              <li>Monitor and manage PM2 processes in real-time</li>
              <li>Deploy new apps, configure ecosystems, manage clusters</li>
              <li>Remote server SSH connections with live logs</li>
              <li>Cron job scheduling and dark-mode support</li>
            </ul>

            <Divider sx={{ my: 2 }} />

            <a
              href="https://github.com/thechandanbhagat/ezpm2gui"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8125rem', color: '#6366f1', textDecoration: 'none' }}
            >
              <GitHubIcon sx={{ fontSize: 15 }} />
              GitHub Repository ↗
            </a>
          </DialogContent>
        </Dialog>
      </div>
    </Router>
    </ThemeProvider>
  );
};

export default App;
